import React, { useState, useEffect } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import { validatePassword } from "../utils/validators";
import Button from "../components/common/Button";
import Input from "../components/common/Input";
import { Card } from "../components/ui/Card";
import LoadingSpinner from "../components/common/LoadingSpinner";
import Toast from "../components/common/Toast";
import { useTheme } from "../context/ThemeContext";

const RESET_PASSWORD_ENDPOINTS = [
  `${import.meta.env.VITE_BACKEND_API_BASE_URL || ""}/reset-password.php`,
  `${import.meta.env.VITE_AUTH_API_BASE_URL || ""}/reset-password.php`,
  "https://gainwisefx.com/api/reset-password.php",
  "/api/reset-password.php",
].filter((value, index, list) => value && list.indexOf(value) === index);

const VALIDATE_RESET_TOKEN_ENDPOINTS = [
  `${import.meta.env.VITE_BACKEND_API_BASE_URL || ""}/validate-reset-token.php`,
  `${import.meta.env.VITE_AUTH_API_BASE_URL || ""}/validate-reset-token.php`,
  "https://gainwisefx.com/api/validate-reset-token.php",
  "/api/validate-reset-token.php",
].filter((value, index, list) => value && list.indexOf(value) === index);

const ResetPassword = () => {
  const [formData, setFormData] = useState({
    password: "",
    confirmPassword: "",
  });
  const [errors, setErrors] = useState({});
  const [toast, setToast] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isValidToken, setIsValidToken] = useState(null);
  const [isSuccess, setIsSuccess] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [passwordStrength, setPasswordStrength] = useState(0);

  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const token = searchParams.get("token");
  const { isDark, getThemeConfig } = useTheme();
  const themeConfig = getThemeConfig();

  useEffect(() => {
    if (!token) {
      setIsValidToken(false);
      setToast({
        type: "error",
        message: "Invalid or missing reset token.",
      });
      return;
    }

    // Validate token on component mount
    validateToken();
  }, [token]);

  useEffect(() => {
    if (formData.password) {
      const strength = calculatePasswordStrength(formData.password);
      setPasswordStrength(strength);
    }
  }, [formData.password]);

  const calculatePasswordStrength = (password) => {
    let score = 0;
    if (password.length >= 8) score += 25;
    if (password.match(/[a-z]/)) score += 25;
    if (password.match(/[A-Z]/)) score += 25;
    if (password.match(/[0-9]/)) score += 25;
    if (password.match(/[^a-zA-Z0-9]/)) score += 25;
    return Math.min(score, 100);
  };

  const getPasswordStrengthColor = () => {
    if (passwordStrength < 25) return "bg-red-500";
    if (passwordStrength < 50) return "bg-orange-500";
    if (passwordStrength < 75) return "bg-yellow-500";
    return "bg-green-500";
  };

  const getPasswordStrengthText = () => {
    if (passwordStrength < 25) return "Weak";
    if (passwordStrength < 50) return "Fair";
    if (passwordStrength < 75) return "Good";
    return "Strong";
  };

  const validateToken = async () => {
    try {
      let result = null;

      for (const endpoint of VALIDATE_RESET_TOKEN_ENDPOINTS) {
        try {
          const response = await fetch(endpoint, {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
            },
            body: JSON.stringify({ token }),
          });

          if (!response.ok) {
            continue;
          }

          result = await response.json();
          break;
        } catch {
          continue;
        }
      }

      if (!result) {
        throw new Error("Unable to validate token");
      }
      setIsValidToken(result.success);

      if (!result.success) {
        setToast({
          type: "error",
          message: result.message || "Invalid or expired reset token.",
        });
      }
    } catch (error) {
      console.error("Token validation error:", error);
      setIsValidToken(false);
      setToast({
        type: "error",
        message: "Unable to validate reset token. Please try again.",
      });
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));

    // Clear error when user starts typing
    if (errors[name]) {
      setErrors((prev) => ({
        ...prev,
        [name]: "",
      }));
    }
  };

  const validateForm = () => {
    const newErrors = {};

    // Password validation
    const passwordError = validatePassword(formData.password);
    if (passwordError) newErrors.password = passwordError;

    // Confirm password validation
    if (formData.password !== formData.confirmPassword) {
      newErrors.confirmPassword = "Passwords do not match";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) return;

    setIsSubmitting(true);
    setErrors({});

    try {
      let apiResult = null;

      for (const endpoint of RESET_PASSWORD_ENDPOINTS) {
        try {
          const response = await fetch(endpoint, {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
            },
            body: JSON.stringify({
              token,
              password: formData.password,
            }),
          });

          if (!response.ok) {
            continue;
          }

          apiResult = await response.json();
          break;
        } catch {
          continue;
        }
      }

      if (!apiResult) throw new Error("Network response was not ok");

      if (apiResult.success) {
        setIsSuccess(true);
        setToast({
          type: "success",
          message:
            "Password reset successfully! You can now sign in with your new password.",
        });

        // Redirect to login after 3 seconds
        setTimeout(() => {
          navigate("/login");
        }, 3000);
      } else {
        const errorMessage =
          apiResult.message || "Failed to reset password. Please try again.";

        if (errorMessage.toLowerCase().includes("password")) {
          setErrors({ password: errorMessage });
        } else if (errorMessage.toLowerCase().includes("token")) {
          setToast({
            type: "error",
            message:
              "Reset token has expired or is invalid. Please request a new password reset.",
          });
          setIsValidToken(false);
        } else {
          setToast({
            type: "error",
            message: errorMessage,
          });
        }
      }
    } catch (error) {
      console.error("Error:", error);

      if (!navigator.onLine) {
        setToast({
          type: "error",
          message:
            "No internet connection. Please check your network and try again.",
        });
      } else if (
        error.name === "TypeError" &&
        error.message.includes("fetch")
      ) {
        setToast({
          type: "error",
          message: "Unable to connect to server. Please try again later.",
        });
      } else {
        setToast({
          type: "error",
          message: "An unexpected error occurred. Please try again.",
        });
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  if (isValidToken === null) {
    return (
      <div
        className={`min-h-screen flex items-center justify-center p-4 ${
          isDark
            ? "bg-gradient-to-br from-slate-900 via-blue-900 to-slate-900"
            : "bg-gradient-to-br from-blue-50 via-white to-blue-50"
        }`}
      >
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  if (isValidToken === false) {
    return (
      <div
        className={`min-h-screen flex items-center justify-center p-4 ${
          isDark
            ? "bg-gradient-to-br from-slate-900 via-blue-900 to-slate-900"
            : "bg-gradient-to-br from-blue-50 via-white to-blue-50"
        }`}
      >
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="w-full max-w-md"
        >
          <Card
            className={`backdrop-blur-sm ${
              isDark
                ? "bg-slate-800/50 border-slate-700/50"
                : "bg-white/80 border-gray-200/50"
            }`}
          >
            <div className="p-8 text-center">
              <div className="w-16 h-16 bg-red-100 dark:bg-red-900/20 rounded-full flex items-center justify-center mx-auto mb-4">
                <svg
                  className="w-8 h-8 text-red-600 dark:text-red-400"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M6 18L18 6M6 6l12 12"
                  />
                </svg>
              </div>
              <h1
                className={`text-2xl font-bold mb-2 ${
                  isDark ? "text-white" : "text-gray-900"
                }`}
              >
                Invalid Reset Link
              </h1>
              <p
                className={`mb-6 ${
                  isDark ? "text-slate-400" : "text-gray-600"
                }`}
              >
                This password reset link is invalid or has expired. Please
                request a new password reset.
              </p>
              <div className="space-y-3">
                <Button
                  onClick={() => navigate("/forgot-password")}
                  className="w-full bg-gradient-to-r from-blue-500 to-purple-600 hover:from-blue-600 hover:to-purple-700"
                >
                  Request New Reset Link
                </Button>
                <Button
                  onClick={() => navigate("/login")}
                  variant="outline"
                  className={`w-full ${
                    isDark
                      ? "text-white border-slate-600 hover:bg-slate-700"
                      : "text-gray-700 border-gray-300 hover:bg-gray-50"
                  }`}
                >
                  Back to Login
                </Button>
              </div>
            </div>
          </Card>
        </motion.div>
      </div>
    );
  }

  return (
    <div
      className={`min-h-screen flex items-center justify-center p-4 ${
        isDark
          ? "bg-gradient-to-br from-slate-900 via-blue-900 to-slate-900"
          : "bg-gradient-to-br from-blue-50 via-white to-blue-50"
      }`}
    >
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6 }}
        className="w-full max-w-md"
      >
        <Card
          className={`backdrop-blur-sm ${
            isDark
              ? "bg-slate-800/50 border-slate-700/50"
              : "bg-white/80 border-gray-200/50"
          }`}
        >
          <div className="p-8">
            {/* Header */}
            <div className="text-center mb-8">
              <motion.div
                initial={{ scale: 0.8 }}
                animate={{ scale: 1 }}
                transition={{ duration: 0.5 }}
                className="w-16 h-16 bg-gradient-to-r from-blue-500 to-purple-600 rounded-full flex items-center justify-center mx-auto mb-4"
              >
                <span className="text-2xl font-bold text-white">T</span>
              </motion.div>
              <h1
                className={`text-2xl font-bold mb-2 ${
                  isDark ? "text-white" : "text-gray-900"
                }`}
              >
                {isSuccess ? "Password Reset Complete" : "Reset Your Password"}
              </h1>
              <p className={isDark ? "text-slate-400" : "text-gray-600"}>
                {isSuccess
                  ? "Your password has been successfully reset. You can now sign in with your new password."
                  : "Enter your new password below."}
              </p>
            </div>

            {!isSuccess ? (
              /* Reset Form */
              <form onSubmit={handleSubmit} className="space-y-6">
                <div>
                  <div className="relative">
                    <Input
                      type={showPassword ? "text" : "password"}
                      name="password"
                      placeholder="Enter new password"
                      value={formData.password}
                      onChange={handleInputChange}
                      error={errors.password}
                      className={`pr-10 ${
                        isDark
                          ? "bg-slate-700/50 border-slate-600 text-white placeholder-slate-400"
                          : "bg-white border-gray-300 text-gray-900 placeholder-gray-500"
                      }`}
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className={`absolute right-3 top-1/2 transform -translate-y-1/2 ${
                        isDark
                          ? "text-slate-400 hover:text-white"
                          : "text-gray-500 hover:text-gray-700"
                      }`}
                    >
                      {showPassword ? "🙈" : "👁️"}
                    </button>
                  </div>

                  {/* Password Strength Indicator */}
                  {formData.password && (
                    <div className="mt-2">
                      <div className="flex items-center space-x-2">
                        <div
                          className={`flex-1 rounded-full h-2 ${
                            isDark ? "bg-slate-600" : "bg-gray-300"
                          }`}
                        >
                          <div
                            className={`h-2 rounded-full transition-all duration-300 ${getPasswordStrengthColor()}`}
                            style={{ width: `${passwordStrength}%` }}
                          />
                        </div>
                        <span
                          className={`text-xs ${
                            isDark ? "text-slate-400" : "text-gray-500"
                          }`}
                        >
                          {getPasswordStrengthText()}
                        </span>
                      </div>
                    </div>
                  )}
                </div>

                <div className="relative">
                  <Input
                    type={showConfirmPassword ? "text" : "password"}
                    name="confirmPassword"
                    placeholder="Confirm new password"
                    value={formData.confirmPassword}
                    onChange={handleInputChange}
                    error={errors.confirmPassword}
                    className={`pr-10 ${
                      isDark
                        ? "bg-slate-700/50 border-slate-600 text-white placeholder-slate-400"
                        : "bg-white border-gray-300 text-gray-900 placeholder-gray-500"
                    }`}
                  />
                  <button
                    type="button"
                    onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                    className={`absolute right-3 top-1/2 transform -translate-y-1/2 ${
                      isDark
                        ? "text-slate-400 hover:text-white"
                        : "text-gray-500 hover:text-gray-700"
                    }`}
                  >
                    {showConfirmPassword ? "🙈" : "👁️"}
                  </button>
                </div>

                <Button
                  type="submit"
                  className="w-full bg-gradient-to-r from-blue-500 to-purple-600 hover:from-blue-600 hover:to-purple-700 disabled:opacity-50 disabled:cursor-not-allowed"
                  disabled={isSubmitting}
                >
                  {isSubmitting ? (
                    <div className="flex items-center justify-center space-x-2">
                      <LoadingSpinner size="sm" />
                      <span>Resetting Password...</span>
                    </div>
                  ) : (
                    "Reset Password"
                  )}
                </Button>
              </form>
            ) : (
              /* Success State */
              <div className="space-y-6">
                <div className="text-center">
                  <div className="w-16 h-16 bg-green-100 dark:bg-green-900/20 rounded-full flex items-center justify-center mx-auto mb-4">
                    <svg
                      className="w-8 h-8 text-green-600 dark:text-green-400"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M5 13l4 4L19 7"
                      />
                    </svg>
                  </div>
                  <p
                    className={`text-sm ${
                      isDark ? "text-slate-300" : "text-gray-600"
                    }`}
                  >
                    Redirecting to login page in a few seconds...
                  </p>
                </div>

                <Button
                  onClick={() => navigate("/login")}
                  className="w-full bg-gradient-to-r from-blue-500 to-purple-600 hover:from-blue-600 hover:to-purple-700"
                >
                  Sign In Now
                </Button>
              </div>
            )}

            {/* Back to Login Link */}
            {!isSuccess && (
              <div className="mt-6 text-center">
                <p className={isDark ? "text-slate-400" : "text-gray-600"}>
                  Remember your password?{" "}
                  <Link
                    to="/login"
                    className={`font-medium transition-colors ${
                      isDark
                        ? "text-blue-400 hover:text-blue-300"
                        : "text-blue-600 hover:text-blue-500"
                    }`}
                  >
                    Back to Sign In
                  </Link>
                </p>
              </div>
            )}
          </div>
        </Card>
      </motion.div>

      {/* Toast Notifications */}
      {toast && (
        <Toast
          type={toast.type}
          message={toast.message}
          onClose={() => setToast(null)}
        />
      )}
    </div>
  );
};

export default ResetPassword;
